import type {AppSyncMessage} from '../types';
import {captureWebsocketError} from '../utils/captureWebsocketError';
import {handleIncomingData} from '../utils/handleIncomingData';
import {handleWebSocketMessage} from '../utils/handleWebSocketMessage';
import {appendNotification} from './appendNotification';
import {handleSubscriptionAck} from './handleSubscriptionAck';
import {registerSubscription} from './registerSubscription';
import {reportErrorAndMaybeRefresh} from './reportErrorAndMaybeRefresh';
import {resetKeepAlive} from './resetKeepAlive';
import type {SocketContext, SubscriptionDeps, SubscriptionState} from './subscriptionTypes';

/**
 * Routes an incoming server message to the right handler. Parsing is wrapped in
 * try/catch because the wire format is untrusted — a malformed frame must not
 * tear the loop down.
 */
export const handleSocketMessage = (
  event: MessageEvent<string>,
  state: SubscriptionState,
  deps: SubscriptionDeps,
  ctx: SocketContext
) => {
  try {
    const message = JSON.parse(event.data) as AppSyncMessage;

    handleWebSocketMessage(message, {
      onConnectionAck: (keepAliveTimeoutMs) => registerSubscription(keepAliveTimeoutMs, state, ctx),
      onSubscriptionAck: () => handleSubscriptionAck(state, deps),
      onData: handleIncomingData(deps.dispatch, (notification) =>
        deps.updateCachedData((draft) => appendNotification(draft, notification))
      ),
      onError: (payload) => reportErrorAndMaybeRefresh(payload, state, deps, ctx),
      onComplete: () => ctx.socket.close(),
      onKeepAlive: () => resetKeepAlive(ctx),
    });
  } catch (error) {
    captureWebsocketError(error);
  }
};
